import { For, Index, Match, Show, Switch } from "solid-js";
import Text from "./Text";
import TaskParameterInput from "./TaskParameterInput";
import { useTranslation } from "../hooks/useTranslation";
import styles from "./ConditionTreeEditor.module.css";

export interface ConditionParam {
    key: string;
    type: string;
    options?: readonly string[] | null;
    optionSource?: string | null;
}

export type ConditionLeaf = { param: string; op: string; value: string };
export type ConditionGroup = { group: "and" | "or"; children: ConditionNode[] };
export type ConditionNode = ConditionGroup | ConditionLeaf;

export const isGroup = (node: ConditionNode): node is ConditionGroup => "group" in node;

// Matches ConditionOperator on the backend. Kept in one place so the editor can never offer a
// comparison the evaluator would reject at save time.
const OPERATORS = ["eq", "ne", "lt", "le", "ge", "gt", "contains", "startswith", "endswith", "exists"] as const;

const TEXT_ONLY_OPERATORS = new Set(["contains", "startswith", "endswith"]);

export const MAX_CONDITION_DEPTH = 10;

export const emptyGroup = (): ConditionGroup => ({ group: "and", children: [] });

interface Props {
    node: ConditionGroup;
    params: readonly ConditionParam[];
    onChange: (next: ConditionGroup) => void;
    depth?: number;
    // Values picked from a bot's own lists (its sell offers) need the bot to load them from.
    botId?: string;
}

export default function ConditionTreeEditor(props: Props) {
    const { t } = useTranslation();

    const depth = () => props.depth ?? 1;

    const operatorsFor = (paramKey: string) => {
        const param = props.params.find(p => p.key === paramKey);
        if (!param) return OPERATORS;
        return param.type === "number" || param.type === "datetime"
            ? OPERATORS.filter(op => !TEXT_ONLY_OPERATORS.has(op))
            : OPERATORS;
    };

    const replaceChild = (index: number, child: ConditionNode) => {
        const children = [...props.node.children];
        children[index] = child;
        props.onChange({ ...props.node, children });
    };

    const removeChild = (index: number) => {
        props.onChange({ ...props.node, children: props.node.children.filter((_, i) => i !== index) });
    };

    const addLeaf = () => {
        const first = props.params[0];
        props.onChange({
            ...props.node,
            children: [...props.node.children, { param: first?.key ?? "", op: "eq", value: "" }],
        });
    };

    const addGroup = () => {
        props.onChange({ ...props.node, children: [...props.node.children, emptyGroup()] });
    };

    return (
        <div class={`${styles.group} ${depth() > 1 ? styles.nested : ""}`}>
            <div class={styles.groupHead}>
                <span class={styles.groupLabel}><Text val="AUTOMATION_CONDITION_MATCH" /></span>
                <select
                    value={props.node.group}
                    onChange={(e) => props.onChange({ ...props.node, group: e.currentTarget.value as "and" | "or" })}
                >
                    <option value="and">{t("AUTOMATION_CONDITION_ALL")}</option>
                    <option value="or">{t("AUTOMATION_CONDITION_ANY")}</option>
                </select>
            </div>

            <Show
                when={props.node.children.length > 0}
                fallback={<span class={styles.empty}><Text val="AUTOMATION_CONDITION_NONE" /></span>}
            >
                <div class={styles.children}>
                    <Index each={props.node.children}>
                        {(child, index) => (
                            <Show
                                when={isGroup(child())}
                                fallback={
                                    <div class={styles.leaf}>
                                        <select
                                            value={(child() as ConditionLeaf).param}
                                            onChange={(e) => replaceChild(index, { ...(child() as ConditionLeaf), param: e.currentTarget.value })}
                                        >
                                            <For each={props.params}>{(param) => <option value={param.key}>{param.key}</option>}</For>
                                        </select>

                                        <select
                                            value={(child() as ConditionLeaf).op}
                                            onChange={(e) => replaceChild(index, { ...(child() as ConditionLeaf), op: e.currentTarget.value })}
                                        >
                                            <For each={operatorsFor((child() as ConditionLeaf).param)}>
                                                {(op) => <option value={op}>{t(`AUTOMATION_OP_${op.toUpperCase()}`)}</option>}
                                            </For>
                                        </select>

                                        <Show when={(child() as ConditionLeaf).op !== "exists"}>
                                            <ValueField
                                                botId={props.botId}
                                                param={props.params.find(p => p.key === (child() as ConditionLeaf).param)}
                                                value={(child() as ConditionLeaf).value}
                                                onInput={(value) => replaceChild(index, { ...(child() as ConditionLeaf), value })}
                                            />
                                        </Show>

                                        <button
                                            type="button"
                                            class="btn btn-danger btn-sm btn-icon"
                                            title={t("AUTOMATION_CONDITION_REMOVE")}
                                            aria-label={t("AUTOMATION_CONDITION_REMOVE")}
                                            onClick={() => removeChild(index)}
                                        >
                                            <i class="fa-solid fa-xmark" />
                                        </button>
                                    </div>
                                }
                            >
                                <div class={styles.leaf} style={{ "align-items": "flex-start" }}>
                                    <div style={{ flex: "1 1 auto", "min-width": 0 }}>
                                        <ConditionTreeEditor
                                            node={child() as ConditionGroup}
                                            params={props.params}
                                            botId={props.botId}
                                            depth={depth() + 1}
                                            onChange={(next) => replaceChild(index, next)}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        class="btn btn-danger btn-sm btn-icon"
                                        title={t("AUTOMATION_CONDITION_REMOVE_GROUP")}
                                        aria-label={t("AUTOMATION_CONDITION_REMOVE_GROUP")}
                                        onClick={() => removeChild(index)}
                                    >
                                        <i class="fa-solid fa-xmark" />
                                    </button>
                                </div>
                            </Show>
                        )}
                    </Index>
                </div>
            </Show>

            <div class={styles.actions}>
                <button type="button" class="btn btn-secondary btn-sm" disabled={props.params.length === 0} onClick={addLeaf}>
                    <i class="fa-solid fa-plus" /> <Text val="AUTOMATION_CONDITION_ADD" />
                </button>
                <Show when={depth() < MAX_CONDITION_DEPTH}>
                    <button type="button" class="btn btn-secondary btn-sm" onClick={addGroup}>
                        <i class="fa-solid fa-code-branch" /> <Text val="AUTOMATION_CONDITION_ADD_GROUP" />
                    </button>
                </Show>
            </div>
        </div>
    );
}

function ValueField(props: { param?: ConditionParam; botId?: string; value: string; onInput: (value: string) => void }) {
    return (
        <Switch
            fallback={
                <input
                    type={props.param?.type === "number" ? "number" : "text"}
                    value={props.value}
                    onInput={(e) => props.onInput(e.currentTarget.value)}
                />
            }
        >
            <Match when={props.param?.optionSource && props.botId}>
                <TaskParameterInput
                    botId={props.botId!}
                    parameter={{ name: props.param!.key, type: props.param!.type, required: true, optionSource: props.param!.optionSource }}
                    value={props.value}
                    onChange={props.onInput}
                />
            </Match>
            <Match when={props.param?.options && props.param.options.length > 0}>
                <select value={props.value} onChange={(e) => props.onInput(e.currentTarget.value)}>
                    <For each={props.param!.options!}>{(option) => <option value={option}>{option}</option>}</For>
                </select>
            </Match>
        </Switch>
    );
}
